import React, { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BoxArrowInRight, CheckCircleFill, XCircleFill, XSquare } from "react-bootstrap-icons";

import useApis from "../../services/useAPI";
import AccountCard from "../../stories/cards/AccountCard";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { sendOtpUrl, verifyOtpUrl } from "../../utils/constants";
import { setMfaTimestamp } from "./MFAExpiry";

const OTP_INPUT_IDS = ["otp-0", "otp-1", "otp-2", "otp-3", "otp-4", "otp-5"];
const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60;

const ConfirmOtp = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { callApi: verifyOtpCallApi, resultPost, serverError } = useApis(verifyOtpUrl, "POST");
  const { callApi: resendOtpCallApi } = useApis(sendOtpUrl, "POST");
  const isPhlebotomist = JSON.parse(localStorage.getItem("is_phlebotomist") || "false");
  const inputRefs = useRef([]);

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [otpError, setOtpError] = useState("");
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [canResend, setCanResend] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);

  const otpString = otp.join("");
  const isValidOtp = otpString.length === OTP_LENGTH;
  const emailOtp = auth.isNonUKNumber;
  useEffect(() => {
    if (localStorage.getItem("mfa_pending") !== "true") {
      navigate(auth?.isAuthenticated ? "/" : "/login", { replace: true });
    }
  }, []);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Resend countdown timer
  useEffect(() => {
    if (resendTimer <= 0) {
      setCanResend(true);
      return;
    }
    const interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Handle successful OTP verification
  useEffect(() => {
    if (!resultPost?.data?.access_token) return;

    setMfaTimestamp();

    Cookies.set("access_token", resultPost.data.access_token, {
      secure: true,
      sameSite: "strict",
      expires: 2 / 24,
    });
    Cookies.set("refresh_token", resultPost.data.refresh_token, {
      secure: true,
      sameSite: "strict",
      expires: 2 / 24,
    });

    localStorage.removeItem("mfa_pending");
    localStorage.setItem("isLoggedIn", true);
    localStorage.setItem("username", resultPost.data.fullname);
    localStorage.setItem("role", resultPost.data.role);
    localStorage.setItem("user_id", resultPost.data.id);

    auth.setIsAuthenticated(true);
    setOtpSuccess(true);
    setLoading(false);

    auth?.showToast("Logged in", "Logged in successfully!", "../assets/mhc.png", <BoxArrowInRight />);
    if (isPhlebotomist) {
      navigate("/appointments");
    } else {
      navigate("/");
    }
  }, [resultPost?.data?.access_token]);

  // Handle server errors
  useEffect(() => {
    if (!serverError) return;
    setOtpError(serverError);
    setOtpSuccess(false);
    setLoading(false);
    auth?.showToast("Invalid OTP", serverError, "../assets/mhc.png", <XSquare />);
  }, [serverError]);

  const resetOtpState = () => {
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpError("");
    setOtpSuccess(false);
  };

  const handleInputChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    if (otpSuccess) setOtpSuccess(false);
    if (otpError) setOtpError("");

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      if (otp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, OTP_LENGTH);
    if (!/^\d*$/.test(pastedData)) return;

    const newOtp = pastedData.split("");
    while (newOtp.length < OTP_LENGTH) newOtp.push("");
    setOtp(newOtp.slice(0, OTP_LENGTH));
    setOtpError("");
    setOtpSuccess(false);

    const nextEmptyIndex = newOtp.findIndex((val) => !val);
    setTimeout(() => inputRefs.current[nextEmptyIndex === -1 ? OTP_LENGTH - 1 : nextEmptyIndex]?.focus(), 0);
  };

  const handleVerifyOtp = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!isValidOtp) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    setOtpError("");
    setOtpSuccess(false);

    verifyOtpCallApi({
      otp: otpString,
      user_id: JSON.parse(localStorage.getItem("user_id")),
    });
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    try {
      await resendOtpCallApi({
        user_id: JSON.parse(localStorage.getItem("user_id")),
        role: "customer",
        email_mfa:auth.isNonUKNumber
      });
      auth.showToast("Resent", "OTP sent successfully.", "../assets/mhc.png", <BoxArrowInRight />);
      resetOtpState();
      setCanResend(false);
      setResendTimer(RESEND_COOLDOWN);
      inputRefs.current[0]?.focus();
    } catch {
      auth.showToast("Error", "Failed to resend OTP", "../assets/mhc.png", <XSquare />);
    } finally {
      setResendLoading(false);
    }
  };

  const renderResendContent = () => {
    if (resendLoading) return (
      <>
        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="otp-spinner" />
        <span>Sending...</span>
      </>
    );
    if (canResend) return <span>Resend Code</span>;
    return <span className="resend-timer">Resend in {resendTimer}s</span>;
  };

  if (loading && !otpSuccess) {
    return (
      <div className="text-center">
        <Spinner animation="border" role="output" data-testid="spinner" variant="info" className="my-custom-spinner" />
      </div>
    );
  }

  return (
    <AccountCard className="justify-content-center" title="Confirm OTP" isLoading={loading} finished={false} verifyPage={true}>
      <div className="otp-verification-container">

        <div className="otp-header">
          <p className="otp-subtitle ageo-medium">
            Please enter the verification code sent to your registered {emailOtp ? "email" : "mobile number"} to complete log in
          </p>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="otp-form" onKeyDown={(e) => {
          if (e.key === "Enter" && isValidOtp && !otpSuccess) {
            handleVerifyOtp();
          }
        }}>
          <div className="otp-inputs-wrapper">
            <div className="otp-inputs-grid">
              {otp.map((digit, index) => (
                <div key={OTP_INPUT_IDS[index]} className="otp-input-wrapper">
                  <input
                    ref={(el) => (inputRefs.current[index] = el)}
                    id={OTP_INPUT_IDS[index]}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className={`otp-input ${digit ? "filled" : ""} ${otpError ? "error" : ""} ${otpSuccess ? "success" : ""}`}
                    placeholder=""
                    disabled={loading || otpSuccess}
                    autoComplete="one-time-code"
                  />
                  <div className="otp-input-underline" />
                </div>
              ))}
            </div>

            {otpSuccess && (
              <div className="otp-success-message mx-auto">
                <CheckCircleFill size={18} />
                <span className="ageo-medium">Code verified successfully!</span>
              </div>
            )}

            {otpError && !otpSuccess && (
              <div className="otp-error-message mx-auto">
                <XCircleFill size={18} />
                <span className="ageo-medium">{otpError}</span>
              </div>
            )}
          </div>
        </form>

        <div className="row justify-content-center">
          <div className="col-md-8 col-12 px-2">
            <button
              type="button"
              onClick={handleVerifyOtp}
              className="btn btn-green w-100 mb-4"
              disabled={!isValidOtp || otpSuccess}
            >
              Confirm OTP
            </button>
          </div>
        </div>

        <div className="otp-resend-section" style={{ paddingTop: 0 }}>
          <p className="otp-resend-text text-muted ageo-medium mb-0">
            Didn't receive the code?
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resendLoading || !canResend || loading || otpSuccess}
              className="otp-resend-btn px-0 mx-1 ageo-semibold"
            >
              {renderResendContent()}
            </button>
          </p>
        </div>

      </div>
    </AccountCard>
  );
};

export default ConfirmOtp;